import {
    NewOrderBase,
    NewOrderBatch,
    OrderSchema,
    OrderUpdateReport,
} from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { Order } from '@thinkalpha/trading-ws-client/contracts/order.js';
import { AccountsJoinedOrLeft, ReconcileAccounts } from '@thinkalpha/trading-ws-client/contracts/push.js';
/*
 * The trading socket worker contract pair (ADR 0016): types plus the value-level manifest,
 * importable by both the main bundle and the worker bundle without dragging worker code into
 * either. Request/response/event payloads are the shared @thinkalpha/trading-ws-client
 * contracts, so the main↔worker wire validates against the same schemas the trading server
 * speaks — no locally redefined structural types, no replacer/reviver framing.
 */
import { defineWorkerManifest } from 'src/services/workerHost/contract';
import type { WorkerContract } from 'src/services/workerHost/contract';
import { z } from 'zod';

export const TRADING_SOCKET_WORKER_STATES = [
    'initial',
    'connecting',
    'connected',
    'authenticating',
    'authenticated',
    'reconciling',
] as const;

/** Worker connection state machine states. */
export type TradingSocketWorkerState = (typeof TRADING_SOCKET_WORKER_STATES)[number];

export interface TradingSocketWorkerContract extends WorkerContract {
    requests: {
        /**
         * Replace the worker's target account set — the union across every main-thread
         * consumer, computed by the spawning tab. The response is an acknowledgement only;
         * server-confirmed joins arrive as `account.joined` events.
         */
        'account.reconcile': { request: ReconcileAccounts; response: null };
        'order.create': { request: NewOrderBase; response: Order };
        'order.batch-create': { request: NewOrderBatch; response: { orders: Order[] } };
    };
    events: {
        'state-changed': { state: TradingSocketWorkerState };
        /**
         * The trading server rejected the worker's current token. Refresh custody stays on the
         * spawning thread: the client routes this into `SessionService.refresh(rejectedToken)`.
         */
        'authentication-rejected': { rejectedToken: string };
        'account.joined': AccountsJoinedOrLeft;
        'account.left': AccountsJoinedOrLeft;
        'order.updated': OrderUpdateReport;
        'order.snapshot': Order;
    };
}

/**
 * The `order.batch-create` response schema. Also the shape of the trading server's ack body,
 * which the worker returns verbatim once decoded — one declaration serves both wires.
 */
export const BatchOrdersResponse = z.object({ orders: z.array(OrderSchema) });

export const tradingSocketWorkerManifest = defineWorkerManifest<TradingSocketWorkerContract>({
    state: ['session'],
    codec: 'zod',
    schemas: {
        requests: {
            'account.reconcile': { request: ReconcileAccounts, response: z.null() },
            'order.create': { request: NewOrderBase, response: OrderSchema },
            'order.batch-create': { request: NewOrderBatch, response: BatchOrdersResponse },
        },
        events: {
            'state-changed': z.object({ state: z.enum(TRADING_SOCKET_WORKER_STATES) }),
            'authentication-rejected': z.object({ rejectedToken: z.string() }),
            'account.joined': AccountsJoinedOrLeft,
            'account.left': AccountsJoinedOrLeft,
            'order.updated': OrderUpdateReport,
            'order.snapshot': OrderSchema,
        },
    },
});
